import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Play,
  ArrowUpRight,
  Search,
  Youtube,
  Headphones,
  Podcast as PodcastIcon,
  Rss,
  Mail,
} from "lucide-react";
import { mailtoLink } from "../config/contacts";

/* ── Static data ─────────────────────────────────────────────────────── */

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/oiuyhizp_fad0e5ea-1712-413b-9f1d-c05784213e9c.png";

const IMG = {
  bahrain:    "/about-bahrain.png",
  classroom:  "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/709a95ya_me%20teaching%20math%20v2.png",
  uae:        "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/8s9ay3zs_me%20studying%20in%20uae%20v2.png",
  egypt:      "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/7cwhs84d_me%20studying%20in%20Egypt.png",
  legacy:     "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/q5ci8zh7_ChatGPT%20Image%20May%2023%2C%202026%2C%2010_14_37%20PM.png",
  article:    "https://customer-assets.emergentagent.com/job_hero-design-6/artifacts/esxipfuw_ChatGPT%20Image%20May%2023%2C%202026%2C%2010_13_13%20PM%20%282%29.png",
  arch:       "https://images.unsplash.com/photo-1564769662533-4f00a87b4056?auto=format&fit=crop&w=1200&q=80",
  mosque:     "https://images.unsplash.com/photo-1542816417-0983c9c9ad53?auto=format&fit=crop&w=1200&q=80",
  audience:   "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1200&q=80",
  shelves:    "https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=1200&q=80",
};

const YT_E1 = "https://www.youtube.com/watch?v=Fd04JdWLm5Y";
const ytThumb = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

const START_HERE = [
  {
    n: "01",
    title: "Episode 1: Why The Murabbi Podcast?",
    line: "An unhurried opening — why these conversations had to be recorded, and who they are for.",
    image: ytThumb("Fd04JdWLm5Y"),
    href: "https://www.youtube.com/watch?v=Fd04JdWLm5Y",
    meta: "",
  },
  {
    n: "02",
    title: "Majlis in Ramadan — Interview on Sharjah TV.",
    line: "A Ramadan conversation on faith, formation, and the inner life of the educator.",
    image: ytThumb("2laTbuXfFNA"),
    href: "https://youtu.be/2laTbuXfFNA",
    meta: "",
  },
  {
    n: "03",
    title: "The Real Reason I Restarted This Channel — Part 2.",
    line: "On returning to the work, why now, and what these conversations are really for.",
    image: ytThumb("ASrBdcVnANk"),
    href: "https://youtu.be/ASrBdcVnANk",
    meta: "",
  },
];

const FEATURED = [
  {
    n: "EP 02",
    title: "Advice to Teachers — Fear Allah!",
    excerpt:
      "A direct, brotherly address to those who carry the weight of teaching — and the gravity of the trust placed in them.",
    image: ytThumb("EtzwkbQSaDs"),
    href: "https://youtu.be/EtzwkbQSaDs",
    category: "Education",
  },
  {
    n: "EP 03",
    title: "Teachers! Beware of Arrogance.",
    excerpt:
      "Of all the diseases of the heart that quietly visit a teacher, this is the one that does the most damage.",
    image: ytThumb("CsBfOIq7vCE"),
    href: "https://youtu.be/CsBfOIq7vCE",
    category: "Formation",
  },
  {
    n: "EP 04",
    title: "Our Salaf and Education.",
    excerpt:
      "Returning to the example of those who came before — what they prioritised, and what we have quietly forgotten.",
    image: ytThumb("Z3zI6QKlZVk"),
    href: "https://youtu.be/Z3zI6QKlZVk",
    category: "Education",
  },
];

const CATEGORIES = [
  "All",
  "Formation",
  "Education",
  "Parenting",
  "Leadership",
  "Spirituality",
  "Mentorship",
  "Youth",
];

const ARCHIVE = [
  {
    title: "Adab Is Caught, Not Taught.",
    excerpt: "The teacher's tone, face, and habits teach more than any lesson plan can.",
    image: IMG.article,
    href: YT_E1,
    duration: "27 MIN",
    category: "Education",
  },
  {
    title: "The Inner Life of the Teacher.",
    excerpt: "Why what we are forms students more than what we say.",
    image: IMG.bahrain,
    href: YT_E1,
    duration: "33 MIN",
    category: "Formation",
  },
  {
    title: "Mentoring Boys Toward Manhood.",
    excerpt: "On presence, patience, and the disappearing rituals of becoming a man.",
    image: IMG.classroom,
    href: YT_E1,
    duration: "44 MIN",
    category: "Mentorship",
  },
  {
    title: "Spirituality Without Excess.",
    excerpt: "Tazkiyah grounded in the Book and the Sunnah — beautiful, sober, alive.",
    image: IMG.mosque,
    href: YT_E1,
    duration: "29 MIN",
    category: "Spirituality",
  },
  {
    title: "Leadership Without Vanity.",
    excerpt: "When the office becomes a mirror — and how to keep your soul through it.",
    image: IMG.uae,
    href: YT_E1,
    duration: "36 MIN",
    category: "Leadership",
  },
  {
    title: "Parenting in the Algorithm Age.",
    excerpt: "Raising children whose attention belongs to something more than the feed.",
    image: IMG.legacy,
    href: YT_E1,
    duration: "47 MIN",
    category: "Parenting",
  },
  {
    title: "The Murabbi's Day.",
    excerpt: "A walk through the unsexy disciplines that quietly shape a teacher.",
    image: IMG.egypt,
    href: YT_E1,
    duration: "31 MIN",
    category: "Formation",
  },
  {
    title: "Conversations With My Younger Self.",
    excerpt: "A letter to the seeker I once was, with the patience I did not yet have.",
    image: IMG.arch,
    href: YT_E1,
    duration: "22 MIN",
    category: "Spirituality",
  },
];

const CLIPS = [
  {
    title: "The Friday Message — It's Okay to Fail.",
    image: ytThumb("9iP3f2MrIlc"),
    href: "https://youtube.com/shorts/9iP3f2MrIlc",
    meta: "Short",
  },
  {
    title: "Fear Allah.",
    image: ytThumb("aG-Ud_yiKyo"),
    href: "https://youtube.com/shorts/aG-Ud_yiKyo",
    meta: "Short",
  },
  {
    title: "Being a Murabbi Is an Act of Worship.",
    image: ytThumb("NsSySwfbQSs"),
    href: "https://youtube.com/shorts/NsSySwfbQSs",
    meta: "Short",
  },
  {
    title: "Discipline Your Kids.",
    image: ytThumb("ciYME9iEv4Y"),
    href: "https://youtube.com/shorts/ciYME9iEv4Y",
    meta: "Short",
  },
  {
    title: "Choose Life — Don't Give Up.",
    image: ytThumb("V3ca7ga0JVY"),
    href: "https://youtube.com/shorts/V3ca7ga0JVY",
    meta: "Short",
  },
];

const PLATFORMS = [
  { label: "YouTube",        href: "https://www.youtube.com/@themurabbichannel", Icon: Youtube },
  { label: "Spotify",        href: "https://open.spotify.com",              Icon: Headphones },
  { label: "Apple Podcasts", href: "https://podcasts.apple.com",            Icon: PodcastIcon },
  { label: "RSS",            href: "#",                                     Icon: Rss },
  { label: "Email",          href: mailtoLink("podcast"),                  Icon: Mail },
];

/* ── Atoms ───────────────────────────────────────────────────────────── */

const Eyebrow = ({ n, label }) => (
  <div className="flex items-center gap-4">
    <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
      {n} — {label}
    </span>
    <span className="h-px w-12 bg-azure-soft/50" />
  </div>
);

const Ambient = ({ variant = "a" }) => {
  const variants = {
    a: "radial-gradient(ellipse 55% 50% at 80% 12%, rgba(74,143,229,0.10), transparent 65%), radial-gradient(ellipse 45% 50% at 8% 92%, rgba(201,169,97,0.05), transparent 70%)",
    b: "radial-gradient(ellipse 50% 50% at 12% 18%, rgba(74,143,229,0.10), transparent 65%), radial-gradient(ellipse 50% 50% at 92% 80%, rgba(74,143,229,0.06), transparent 70%)",
    c: "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(74,143,229,0.10), transparent 65%), radial-gradient(ellipse 40% 40% at 50% 100%, rgba(201,169,97,0.05), transparent 70%)",
  };
  return (
    <>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: variants[variant] }} />
      <div aria-hidden="true" className="pointer-events-none absolute top-0 inset-x-0 h-32" style={{ background: "linear-gradient(to bottom, rgba(6,19,37,0.55), transparent)" }} />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 inset-x-0 h-32" style={{ background: "linear-gradient(to top, rgba(6,19,37,0.4), transparent)" }} />
    </>
  );
};

const ExtLink = ({ href, children, ...rest }) => {
  const external = /^(https?:\/\/|mailto:)/i.test(href || "");
  if (external) {
    const isMail = (href || "").startsWith("mailto:");
    return (
      <a
        href={href}
        {...(isMail ? {} : { target: "_blank", rel: "noopener noreferrer" })}
        {...rest}
      >
        {children}
      </a>
    );
  }
  return <Link to={href} {...rest}>{children}</Link>;
};

/* ── 01 — HERO ───────────────────────────────────────────────────────── */
const Hero = () => (
  <section className="relative overflow-hidden bg-ink">
    <Ambient variant="a" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-14 lg:pb-18">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-8">
          <Eyebrow n="01" label="The Murabbi Podcast" />
          <h1 className="mt-9 font-serif text-white text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.022em]">
            A library of{" "}
            <span className="italic text-azure-soft">conversations.</span>
          </h1>
          <div className="mt-9 h-px w-20 bg-gold/80" />
          <p className="mt-9 text-white/70 text-[16.5px] leading-[1.95] max-w-2xl">
            Slow, unhurried conversations on education, formation, faith,
            leadership, family, and the inner life — recorded for the
            seekers, the builders, and the believers.
          </p>
        </div>

        <div className="lg:col-span-4 lg:flex lg:justify-end">
          <div className="flex flex-wrap gap-3">
            {PLATFORMS.map(({ label, href, Icon }) => (
              <ExtLink
                key={label}
                href={href}
                data-testid={`podcast-platform-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group inline-flex items-center gap-2.5 border border-white/15 hover:border-azure/40 px-4 py-2.5 text-[10.5px] tracking-[0.30em] uppercase text-white/70 hover:text-azure-soft transition-colors"
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                {label}
              </ExtLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── 02 — START HERE ─────────────────────────────────────────────────── */
const StartHere = () => (
  <section data-testid="podcast-start-here" className="relative overflow-hidden bg-ink-deep border-t border-white/5">
    <Ambient variant="b" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-12">
        <div className="lg:col-span-8">
          <Eyebrow n="02" label="Start Here" />
          <h2 className="mt-9 font-serif text-white text-[26px] lg:text-[36px] leading-[1.04] tracking-[-0.02em]">
            Three conversations to{" "}
            <span className="italic text-azure-soft">begin with.</span>
          </h2>
        </div>
        <div className="lg:col-span-4 text-white/60 text-[14.5px] leading-[1.85] lg:text-right">
          For first-time listeners — the doorways into the larger body of work.
        </div>
      </div>

      <div className="max-w-[1100px] grid md:grid-cols-3 gap-5 lg:gap-6">
        {START_HERE.map((s) => (
          <ExtLink
            key={s.n}
            href={s.href}
            data-testid={`podcast-start-${s.n}`}
            className="group relative block border border-white/10 hover:border-azure/40 bg-ink-soft/40 transition-colors duration-500"
          >
            <div className="relative aspect-video overflow-hidden">
              <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-deep/60 via-transparent to-transparent" />
              <span className="absolute top-3 left-4 font-serif italic text-azure-soft text-[20px] leading-none">{s.n}</span>
            </div>
            <div className="p-5">
              <h3 className="font-serif text-white text-[15.5px] lg:text-[16.5px] leading-[1.25] tracking-[-0.005em] group-hover:text-azure-soft transition-colors duration-500">
                {s.title}
              </h3>
              <p className="mt-2 text-white/60 text-[12.5px] leading-[1.7]">
                {s.line}
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-azure-soft">
                Begin
                <ArrowUpRight className="w-3 h-3 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </div>
          </ExtLink>
        ))}
      </div>
    </div>
  </section>
);

/* ── 03 — FEATURED ROWS ──────────────────────────────────────────────── */
const Featured = () => (
  <section data-testid="podcast-featured" className="relative overflow-hidden bg-ink">
    <Ambient variant="c" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-12">
        <div className="lg:col-span-8">
          <Eyebrow n="03" label="Featured Conversations" />
          <h2 className="mt-9 font-serif text-white text-[26px] lg:text-[36px] leading-[1.04] tracking-[-0.02em]">
            Cornerstone{" "}
            <span className="italic text-azure-soft">episodes.</span>
          </h2>
        </div>
      </div>

      <div className="space-y-5 lg:space-y-6">
        {FEATURED.map((e) => (
          <ExtLink
            key={e.n}
            href={e.href}
            data-testid={`podcast-featured-${e.n}`}
            className="group grid lg:grid-cols-12 gap-0 border border-white/10 hover:border-azure/40 bg-ink-soft/40 overflow-hidden transition-colors duration-500"
          >
            <div className="relative lg:col-span-3 aspect-video lg:aspect-auto overflow-hidden">
              <img src={e.image} alt={e.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink-deep/30 via-transparent to-ink-deep/20" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="w-11 h-11 rounded-full bg-white/10 backdrop-blur border border-white/40 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                  <Play className="w-3.5 h-3.5 text-white fill-current" strokeWidth={0} />
                </span>
              </div>
            </div>
            <div className="lg:col-span-9 p-6 lg:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-4 text-[10.5px] tracking-[0.34em] uppercase">
                <span className="text-azure-soft">{e.n}</span>
                <span className="h-px w-8 bg-azure-soft/40" />
                <span className="text-white/55">{e.category}</span>
              </div>
              <h3 className="mt-4 font-serif text-white text-[22px] lg:text-[28px] leading-[1.15] tracking-[-0.012em] group-hover:text-azure-soft transition-colors duration-500">
                {e.title}
              </h3>
              <p className="mt-4 text-white/65 text-[14.5px] leading-[1.85] max-w-2xl">
                {e.excerpt}
              </p>
              <div className="mt-6 inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
                Watch on YouTube
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1" />
              </div>
            </div>
          </ExtLink>
        ))}
      </div>
    </div>
  </section>
);

/* ── 04 — WHY THIS PODCAST EXISTS ────────────────────────────────────── */
const Why = () => (
  <section data-testid="podcast-why" className="relative overflow-hidden bg-ink-deep border-t border-white/5">
    <Ambient variant="b" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-18 lg:py-24 text-center">
      <Eyebrow n="04" label="Mission" />
      <h2 className="mt-9 font-serif text-white text-[28px] sm:text-[38px] lg:text-[44px] leading-[1.04] tracking-[-0.02em]">
        Why these conversations{" "}
        <span className="italic text-azure-soft">exist.</span>
      </h2>
      <div className="mt-10 h-px w-20 bg-gold/80 mx-auto" />
      <div className="mt-12 grid lg:grid-cols-3 gap-10 lg:gap-14 max-w-[1280px] mx-auto text-left text-white/75 text-[15.5px] leading-[1.95]">
        <p>
          The Murabbi Podcast was created for those trying to live thoughtfully
          in a distracted age — for Muslim teachers who are seeking to do more
          than teach a curriculum, but to nurture the souls of the future
          leaders of our ummah.
        </p>
        <p>
          We are here to boost the confidence of newly minted teachers, to
          remind those in the trenches why they are doing what they are doing
          on the days that bend them, and to give seasoned veterans a place to
          spread their wisdom.
        </p>
        <p>
          These are conversations on education, formation, faith, leadership,
          family, and the inner life — recorded slowly, honestly, and with the
          belief that meaningful change begins in the heart before it reaches
          the world.
        </p>
      </div>
    </div>
  </section>
);

/* ── 05 — SHORT REFLECTIONS (clips) ──────────────────────────────────── */
const Clips = () => (
  <section data-testid="podcast-clips" className="relative overflow-hidden bg-ink border-t border-white/5">
    <Ambient variant="a" />
    <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-12">
        <div className="lg:col-span-8">
          <Eyebrow n="05" label="Short Reflections" />
          <h2 className="mt-9 font-serif text-white text-[26px] lg:text-[36px] leading-[1.04] tracking-[-0.02em]">
            Begin with a{" "}
            <span className="italic text-azure-soft">moment.</span>
          </h2>
        </div>
        <div className="lg:col-span-4 text-white/60 text-[14.5px] leading-[1.85] lg:text-right">
          30–90 second clips — single lines, sharper edges, faster doors in.
        </div>
      </div>

      <div className="max-w-[1100px] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
        {CLIPS.map((c, i) => (
          <ExtLink
            key={i}
            href={c.href}
            data-testid={`podcast-clip-${i}`}
            className="group relative block border border-white/10 hover:border-azure/40 bg-ink-soft/40 transition-colors duration-500 overflow-hidden"
          >
            <div className="relative aspect-[9/16] overflow-hidden">
              <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/30 to-transparent" />
              <span className="absolute top-3 right-3 text-[9.5px] tracking-[0.30em] uppercase text-white/70">{c.meta}</span>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="w-9 h-9 rounded-full bg-white/10 backdrop-blur border border-white/40 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                  <Play className="w-3 h-3 text-white fill-current" strokeWidth={0} />
                </span>
              </div>
              <div className="absolute left-3 right-3 bottom-3">
                <h3 className="font-serif text-white text-[12.5px] lg:text-[13.5px] leading-[1.25] tracking-[-0.003em]">
                  {c.title}
                </h3>
              </div>
            </div>
          </ExtLink>
        ))}
      </div>
    </div>
  </section>
);

/* ── 06 — ARCHIVE w/ filter + interleaved pull-quote ─────────────────── */
const Archive = () => {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  const filtered = useMemo(() => {
    return ARCHIVE.filter((e) => {
      if (cat !== "All" && e.category !== cat) return false;
      if (q && !`${e.title} ${e.excerpt}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [q, cat]);

  return (
    <section data-testid="podcast-archive" className="relative overflow-hidden bg-ink-deep border-t border-white/5">
      <Ambient variant="b" />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-12">
          <div className="lg:col-span-7">
            <Eyebrow n="06" label="The Archive" />
            <h2 className="mt-9 font-serif text-white text-[26px] lg:text-[36px] leading-[1.04] tracking-[-0.02em]">
              The whole{" "}
              <span className="italic text-azure-soft">library.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:flex lg:justify-end">
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" strokeWidth={1.8} />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search the archive"
                data-testid="podcast-archive-search"
                className="w-full bg-transparent border border-white/15 focus:border-azure/50 outline-none pl-11 pr-4 py-3 text-[13px] text-white placeholder-white/40 tracking-wide transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2.5 mb-10">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`podcast-filter-${c.toLowerCase()}`}
              className={`px-4 py-2 text-[10.5px] tracking-[0.30em] uppercase border transition-colors ${
                cat === c
                  ? "border-azure text-azure bg-azure/10"
                  : "border-white/15 text-white/55 hover:text-white hover:border-white/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Grid + interleaved quote */}
        <div className="grid md:grid-cols-2 gap-6">
          {filtered.map((e, i) => (
            <React.Fragment key={e.title}>
              <ExtLink
                href={e.href}
                data-testid={`podcast-archive-${i}`}
                className="group flex gap-5 border border-white/10 hover:border-azure/40 bg-ink-soft/30 transition-colors duration-500 p-5"
              >
                <div className="relative w-32 sm:w-40 aspect-[4/5] shrink-0 overflow-hidden">
                  <img src={e.image} alt={e.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
                  <div className="absolute inset-0 bg-ink-deep/20 group-hover:bg-ink-deep/0 transition-colors duration-500" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-9 h-9 rounded-full bg-white/10 backdrop-blur border border-white/35 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                      <Play className="w-3 h-3 text-white fill-current" strokeWidth={0} />
                    </span>
                  </span>
                </div>
                <div className="flex-1 min-w-0 flex flex-col py-1">
                  <div className="flex items-center gap-3 text-[10px] tracking-[0.30em] uppercase">
                    <span className="text-azure-soft">{e.category}</span>
                    <span className="text-white/35">· {e.duration}</span>
                  </div>
                  <h3 className="mt-3 font-serif text-white text-[18px] lg:text-[20px] leading-[1.25] tracking-[-0.005em] group-hover:text-azure-soft transition-colors">
                    {e.title}
                  </h3>
                  <p className="mt-3 text-white/60 text-[13.5px] leading-[1.75]">{e.excerpt}</p>
                  <div className="mt-auto pt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-white/45 group-hover:text-azure-soft transition-colors">
                    Watch
                    <ArrowUpRight className="w-3 h-3 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>
              </ExtLink>

              {/* Pull-quote interruption after the 4th archive item */}
              {i === 3 && (
                <figure className="md:col-span-2 my-6 relative p-7 lg:p-10 border-y border-azure-soft/20 bg-ink/30 overflow-hidden">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(ellipse 60% 100% at 100% 0%, rgba(74,143,229,0.10), transparent 70%), radial-gradient(ellipse 50% 80% at 0% 100%, rgba(201,169,97,0.06), transparent 70%)",
                    }}
                  />
                  <span
                    aria-hidden="true"
                    className="absolute -top-2 left-6 font-serif text-azure-soft/35 text-[140px] leading-[0.8] pointer-events-none select-none"
                  >
                    “
                  </span>
                  <blockquote className="relative font-serif text-white text-[26px] lg:text-[34px] leading-[1.25] tracking-[-0.012em] max-w-3xl">
                    Education is not the transfer of information.
                    <span className="block italic text-azure-soft">
                      It is the formation of the human being.
                    </span>
                  </blockquote>
                  <figcaption className="relative mt-6 text-[10.5px] tracking-[0.34em] uppercase text-white/55">
                    — Iyare Omoruyi
                  </figcaption>
                </figure>
              )}
            </React.Fragment>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-20 text-center text-white/45 text-sm">
            No episodes match. Try clearing the filter.
          </div>
        )}
      </div>
    </section>
  );
};

/* ── 07 — CLOSING CTA ────────────────────────────────────────────────── */
const Closing = () => (
  <section className="relative overflow-hidden bg-ink border-t border-white/5">
    <Ambient variant="c" />
    <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-22 text-center">
      <h3 className="font-serif text-white text-[28px] lg:text-[36px] leading-[1.2] tracking-[-0.01em]">
        Follow the conversations as they arrive.
      </h3>
      <div className="mt-10 flex flex-wrap gap-3 justify-center">
        {PLATFORMS.map(({ label, href, Icon }) => (
          <ExtLink
            key={`bottom-${label}`}
            href={href}
            data-testid={`podcast-bottom-platform-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="group inline-flex items-center gap-2.5 border border-white/15 hover:border-azure/40 px-5 py-3 text-[10.5px] tracking-[0.30em] uppercase text-white/70 hover:text-azure-soft transition-colors"
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
            {label}
          </ExtLink>
        ))}
      </div>

      <div className="mt-14 pt-12 border-t border-white/5">
        <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
          Pitches & Collaborations
        </div>
        <h4 className="mt-4 font-serif text-white text-[22px] lg:text-[28px] leading-[1.2] tracking-[-0.012em]">
          Have a story worth recording?
        </h4>
        <a
          href={mailtoLink("podcast", "Podcast Collaboration")}
          data-testid="podcast-become-a-guest-btn"
          className="group mt-7 inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
        >
          Become a Guest
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.8} />
        </a>
      </div>
    </div>
  </section>
);

/* ── PAGE ────────────────────────────────────────────────────────────── */
const Podcast = () => (
  <main data-testid="podcast-page" className="bg-ink">
    <Hero />
    <StartHere />
    <Featured />
    <Why />
    <Clips />
    <Archive />
    <Closing />
  </main>
);

export default Podcast;
